import { z } from 'zod'

const CountrySchema = z.object({
  name: z.string(),
  code: z.string(),
  timezone: z.string(),
})

const NetworkSchema = z.object({
  id: z.number(),
  name: z.string(),
  country: CountrySchema.nullable(),
  officialSite: z.string().nullable(),
})

const WebChannelSchema = z.object({
  id: z.number(),
  name: z.string(),
  country: CountrySchema.nullable(),
  officialSite: z.string().nullable(),
})

const ImageSchema = z
  .object({
    medium: z.string(),
    original: z.string(),
  })
  .nullable()

const RatingSchema = z.object({
  average: z.number().nullable(),
})

const ScheduleSchema = z.object({
  time: z.string(),
  days: z.array(z.string()),
})

const ExternalsSchema = z.object({
  tvrage: z.number().nullable(),
  thetvdb: z.number().nullable(),
  imdb: z.string().nullable(),
})

const LinksSchema = z.object({
  self: z.object({
    href: z.string(),
  }),
  previousepisode: z
    .object({
      href: z.string(),
    })
    .optional(),
  nextepisode: z
    .object({
      href: z.string(),
    })
    .optional(),
})

export const ShowSchema = z.object({
  id: z.number(),
  url: z.string(),
  name: z.string(),
  type: z.string(),
  language: z.string(),
  genres: z.array(z.string()),
  status: z.string(),
  runtime: z.number().nullable(),
  averageRuntime: z.number().nullable(),
  premiered: z.string().nullable(),
  ended: z.string().nullable(),
  officialSite: z.string().nullable(),
  schedule: ScheduleSchema,
  rating: RatingSchema,
  weight: z.number(),
  network: NetworkSchema.nullable(),
  webChannel: WebChannelSchema.nullable(),
  dvdCountry: CountrySchema.nullable(),
  externals: ExternalsSchema,
  image: ImageSchema,
  summary: z.string().nullable(),
  updated: z.number(),
  _links: LinksSchema,
})

export const EpisodeSchema = z.object({
  id: z.number(),
  url: z.string(),
  name: z.string(),
  season: z.number(),
  number: z.number(),
  type: z.string(),
  airdate: z.string(),
  airtime: z.string(),
  airstamp: z.string(),
  runtime: z.number().nullable(),
  rating: RatingSchema,
  image: ImageSchema,
  summary: z.string().nullable(),
  _links: z.object({
    self: z.object({
      href: z.string(),
    }),
    show: z.object({
      href: z.string(),
    }),
  }),
})

export const FollowedShowSchema = z.object({
  show_id: z.number(),
  _embedded: z.object({
    show: ShowSchema,
  }),
})

export const UserEpisodeSchema = z.object({
  episode_id: z.number(),
  marked_at: z.number(),
  type: z.union([z.literal(0), z.literal(1), z.literal(2)]),
})

export const AuthCredentialsSchema = z.object({
  username: z.string().min(1),
  apiKey: z.string().min(1),
})

// Array schemas
export const EpisodesArraySchema = z.array(EpisodeSchema)
export const FollowedShowsArraySchema = z.array(FollowedShowSchema)
export const UserEpisodesArraySchema = z.array(UserEpisodeSchema)

// Type exports
export type Show = z.infer<typeof ShowSchema>
export type Episode = z.infer<typeof EpisodeSchema>
export type FollowedShow = z.infer<typeof FollowedShowSchema>
export type UserEpisode = z.infer<typeof UserEpisodeSchema>
export type AuthCredentials = z.infer<typeof AuthCredentialsSchema>
